import type { FunctionComponent, ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { useLocation } from 'react-router'
import { polygonParam } from '../../../pages/MapPage/query-params'
import useParam, { UrlParam } from '../../hooks/useParam'
import type { ActiveFilter } from '../../components/DataSelectionLegacy/types'
import useLegacyDataselectionConfig from '../../components/DataSelectionLegacy/useLegacyDataselectionConfig'
import { pageParam } from '../../../pages/SearchPage/query-params'
import useBuildQueryString from '../../hooks/useBuildQueryString'
import { DataSelectionContext, type DataSelectionContextProps } from './DataSelectionContext'

import {
  dataSelectionFiltersParam,
  DataSelectionFilters,
} from '../../components/DataSelection/utils/queryParams'

const DataSelectionProvider: FunctionComponent<{ children: ReactNode }> = ({ children }) => {
  const [activeParamFilters, setActiveParamFilters] = useParam(dataSelectionFiltersParam)
  const [polygon] = useParam(polygonParam)
  const [availableFilters, setAvailableFilters] = useState<
    DataSelectionContextProps['availableFilters']
  >([])
  const [totalResults, setTotalResults] = useState(0)
  const { currentDatasetConfig } = useLegacyDataselectionConfig()
  const { buildQueryString } = useBuildQueryString()
  const location = useLocation()

  const addFilter = (filter: DataSelectionFilters) => {
    setActiveParamFilters({
      ...activeParamFilters,
      ...filter,
    })
  }
  const removeFilter = (key: string) => {
    const { [key]: omit, ...rest } = activeParamFilters
    setActiveParamFilters(Object.keys(rest).length ? rest : null)
  }

  const buildFilterLocationDescriptor = ({
    filtersToAdd,
    filtersToRemove,
  }: {
    filtersToAdd?: DataSelectionFilters
    filtersToRemove?: string[]
  }) => {
    let activeParamFiltersCopy: any = { ...activeParamFilters }
    if (filtersToRemove) {
      filtersToRemove.forEach((key) => {
        if (activeParamFiltersCopy[key]) {
          delete activeParamFiltersCopy[key]
        }
      })
    } else if (filtersToAdd) {
      activeParamFiltersCopy = {
        ...activeParamFilters,
        ...filtersToAdd,
      }
    }

    if (!Object.keys(activeParamFiltersCopy).length) {
      activeParamFiltersCopy = null
    }

    const paramsToRemove: Array<UrlParam<any>> = [pageParam]
    if (filtersToRemove?.includes('shape')) {
      paramsToRemove.push(polygonParam)
    }
    return {
      path: location.pathname,
      search: buildQueryString(
        [[dataSelectionFiltersParam, activeParamFiltersCopy]],
        paramsToRemove,
      ),
    }
  }

  const shape = useMemo(
    () =>
      polygon?.polygon?.length
        ? JSON.stringify(polygon.polygon.map(({ lat, lng }) => [lng, lat]))
        : null,
    [polygon],
  )

  const activeFilters = useMemo(() => {
    const filters =
      activeParamFilters && currentDatasetConfig
        ? Object.entries(activeParamFilters).reduce<ActiveFilter[]>((acc, [key, val]) => {
            const label = currentDatasetConfig.FILTERS.find(({ slug }) => slug === key)?.label
            if (label) {
              let value = val
              if (key === 'sbi_code') {
                value = val.replace(/['[\]]/g, '')
              }
              acc.push({ key, value, label: `${label}: ${value}` })
            }
            return acc
          }, [])
        : []

    if (shape) {
      filters.push({
        key: 'shape',
        value: shape,
        label: 'Ingetekende vorm op kaart',
      })
    }

    return filters
  }, [currentDatasetConfig, activeParamFilters, shape])

  return (
    <DataSelectionContext.Provider
      value={{
        // Only provide the token if the user does not have an authenticated session.
        availableFilters,
        activeFilters,
        addFilter,
        removeFilter,
        buildFilterLocationDescriptor,
        totalResults,
        setTotalResults,
        setAvailableFilters,
      }}
    >
      {children}
    </DataSelectionContext.Provider>
  )
}

export default DataSelectionProvider
